<template>
  <div ref="editorRoot" class="tiptap_editor_root">
    <div
      ref="toolbar"
      class="tiptap_toolbar"
      :class="{ is_sticky: isToolbarSticky }"
      :style="stickyToolbarStyle"
    >
      <div class="text_style_controls">
        <el-select
          :value="getTextStyleAttribute('fontFamily')"
          class="font_family_select"
          size="mini"
          placeholder="字体"
          @change="setFontFamily"
        >
          <el-option label="默认字体" value=""></el-option>
          <el-option v-for="font in fontOptions" :key="font.value" :label="font.label" :value="font.value"></el-option>
        </el-select>
        <el-select
          :value="getTextStyleAttribute('fontSize')"
          class="font_size_select"
          size="mini"
          placeholder="字号"
          @change="setFontSize"
        >
          <el-option label="默认字号" value=""></el-option>
          <el-option v-for="size in fontSizeOptions" :key="size" :label="size" :value="size"></el-option>
        </el-select>
        <el-tooltip content="文字颜色" placement="top">
          <el-color-picker
            :value="getTextStyleAttribute('color')"
            size="mini"
            @change="setTextColor"
          ></el-color-picker>
        </el-tooltip>
      </div>

      <el-button-group>
        <el-button size="mini" :type="isActive('bold') ? 'primary' : ''" @click="toggleBold">B</el-button>
        <el-button size="mini" :type="isActive('italic') ? 'primary' : ''" @click="toggleItalic">I</el-button>
        <el-button size="mini" :type="isActive('underline') ? 'primary' : ''" @click="toggleUnderline">U</el-button>
        <el-button size="mini" :type="isActive('strike') ? 'primary' : ''" @click="toggleStrike">S</el-button>
      </el-button-group>

      <el-button-group style="margin-left: 8px;">
        <el-button size="mini" :type="isActive('bulletList') ? 'primary' : ''" @click="toggleBulletList">• 列表</el-button>
        <el-button size="mini" :type="isActive('orderedList') ? 'primary' : ''" @click="toggleOrderedList">1. 列表</el-button>
        <el-button size="mini" :type="isActive('blockquote') ? 'primary' : ''" @click="toggleBlockquote">引用</el-button>
      </el-button-group>

      <el-button-group style="margin-left: 8px;">
        <el-button size="mini" @click="setLink">链接</el-button>
        <el-button size="mini" @click="unsetLink">取消链接</el-button>
      </el-button-group>

      <el-button-group style="margin-left: 8px;">
        <el-button size="mini" :loading="imageUploading" @click="triggerImagePick">上传图片</el-button>
      </el-button-group>

      <el-button-group style="margin-left: 8px;">
        <el-button size="mini" @click="undo" :disabled="!canUndo">撤销</el-button>
        <el-button size="mini" @click="redo" :disabled="!canRedo">重做</el-button>
      </el-button-group>

      <input ref="imageInput" type="file" accept="image/*" style="display:none;" @change="handleImagePicked">
    </div>

    <div
      v-if="isToolbarSticky"
      class="tiptap_toolbar_placeholder"
      :style="{ height: toolbarHeight + 'px' }"
    ></div>

    <bubble-menu
      v-if="editor"
      class="quick_edit_menu"
      :editor="editor"
      :tippy-options="{ duration: 120, placement: 'top', maxWidth: 'none' }"
      :should-show="shouldShowBubbleMenu"
    >
      <div class="quick_edit_group" role="toolbar" aria-label="文字快捷格式">
        <button
          class="quick_edit_button quick_edit_bold"
          :class="{ active: isActive('bold') }"
          type="button"
          title="加粗 (Ctrl+B)"
          aria-label="加粗"
          :aria-pressed="isActive('bold') ? 'true' : 'false'"
          @mousedown.prevent
          @click="toggleBold"
        >B</button>
        <button
          class="quick_edit_button quick_edit_italic"
          :class="{ active: isActive('italic') }"
          type="button"
          title="斜体 (Ctrl+I)"
          aria-label="斜体"
          :aria-pressed="isActive('italic') ? 'true' : 'false'"
          @mousedown.prevent
          @click="toggleItalic"
        >I</button>
        <button
          class="quick_edit_button quick_edit_underline"
          :class="{ active: isActive('underline') }"
          type="button"
          title="下划线 (Ctrl+U)"
          aria-label="下划线"
          :aria-pressed="isActive('underline') ? 'true' : 'false'"
          @mousedown.prevent
          @click="toggleUnderline"
        >U</button>
        <button
          class="quick_edit_button quick_edit_heading"
          :class="{ active: isActive('inlineHeading', { level: 2 }) }"
          type="button"
          title="二级标题（仅选中文字）"
          aria-label="二级标题"
          :aria-pressed="isActive('inlineHeading', { level: 2 }) ? 'true' : 'false'"
          @mousedown.prevent
          @click="toggleInlineHeading(2)"
        >H2</button>
        <button
          class="quick_edit_button quick_edit_heading"
          :class="{ active: isActive('inlineHeading', { level: 3 }) }"
          type="button"
          title="三级标题（仅选中文字）"
          aria-label="三级标题"
          :aria-pressed="isActive('inlineHeading', { level: 3 }) ? 'true' : 'false'"
          @mousedown.prevent
          @click="toggleInlineHeading(3)"
        >H3</button>
        <button
          class="quick_edit_button quick_edit_text_button"
          :class="{ active: isActive('blockquote') }"
          type="button"
          title="引用"
          aria-label="引用"
          :aria-pressed="isActive('blockquote') ? 'true' : 'false'"
          @mousedown.prevent
          @click="toggleBlockquote"
        >引用</button>
        <span class="quick_edit_divider" aria-hidden="true"></span>
        <el-select
          :value="getTextStyleAttribute('fontFamily')"
          class="quick_font_family_select"
          size="mini"
          placeholder="字体"
          aria-label="字体"
          @change="setFontFamily"
        >
          <el-option label="默认字体" value=""></el-option>
          <el-option v-for="font in fontOptions" :key="font.value" :label="font.label" :value="font.value"></el-option>
        </el-select>
        <el-select
          :value="getTextStyleAttribute('fontSize')"
          class="quick_font_size_select"
          size="mini"
          placeholder="字号"
          aria-label="字号"
          @change="setFontSize"
        >
          <el-option label="默认字号" value=""></el-option>
          <el-option v-for="size in fontSizeOptions" :key="size" :label="size" :value="size"></el-option>
        </el-select>
        <span class="quick_edit_divider" aria-hidden="true"></span>
        <button
          class="quick_edit_button quick_edit_text_button"
          :class="{ active: isActive('link') }"
          type="button"
          :title="isActive('link') ? '取消链接' : '添加链接'"
          :aria-label="isActive('link') ? '取消链接' : '添加链接'"
          :aria-pressed="isActive('link') ? 'true' : 'false'"
          @mousedown.prevent
          @click="toggleQuickLink"
        >{{ isActive('link') ? '取消链接' : '链接' }}</button>
        <button
          class="quick_edit_button quick_edit_text_button"
          type="button"
          title="清除文字格式"
          aria-label="清除文字格式"
          @mousedown.prevent
          @click="clearTextFormatting"
        >清除格式</button>
      </div>
    </bubble-menu>

    <el-dialog
      :visible.sync="linkDialogVisible"
      custom-class="link_picker_dialog"
      width="740px"
      :show-close="false"
      append-to-body
      @closed="resetLinkDialog"
    >
      <el-input
        ref="articleLinkInput"
        v-model.trim="articleKeywords"
        class="article_link_input"
        placeholder="搜索文章标题或输入链接"
        clearable
        @keyup.enter.native="handleLinkInputEnter"
      >
        <i
          v-if="articleSearchLoading"
          slot="suffix"
          class="el-icon-loading article_link_loading"
        ></i>
      </el-input>

      <div class="article_link_section_title">
        {{ isLinkUrl(articleKeywords) ? '使用链接' : (articleKeywords ? '搜索结果' : '最新文章') }}
      </div>
      <div v-loading="articleSearchLoading" class="article_link_results">
        <button
          v-if="isLinkUrl(articleKeywords)"
          type="button"
          class="article_link_result"
          @click="applyLink(normalizeLinkUrl(articleKeywords))"
        >
          <span class="article_link_title">{{ articleKeywords }}</span>
          <span class="article_link_meta">按 Enter 使用</span>
        </button>
        <button
          v-for="article in articleSearchResults"
          v-else
          :key="article.id"
          type="button"
          class="article_link_result"
          @click="selectArticleLink(article)"
        >
          <span class="article_link_title">{{ article.title }}</span>
          <span class="article_link_meta">{{ formatArticleDate(article.publish_start) }}</span>
        </button>
        <div
          v-if="!isLinkUrl(articleKeywords) && articleSearchDone && !articleSearchLoading && articleSearchResults.length === 0"
          class="article_link_empty"
        >
          没有找到相关文章
        </div>
      </div>
    </el-dialog>

    <editor-content class="tiptap_editor" :editor="editor" />
  </div>
</template>

<script>
import * as qiniu from 'qiniu-js'
import md5 from 'js-md5'
import { BubbleMenu, Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import TextStyle from '@tiptap/extension-text-style'
import { Extension, Mark, Node, mergeAttributes } from '@tiptap/core'
import { TextSelection } from '@tiptap/pm/state'

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes () {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize || null,
            renderHTML: attributes => attributes.fontSize
              ? { style: `font-size: ${attributes.fontSize}` }
              : {}
          }
        }
      }
    ]
  }
})

const InlineHeading = Mark.create({
  name: 'inlineHeading',
  addAttributes () {
    return {
      level: { default: 2 }
    }
  },
  parseHTML () {
    return [
      {
        tag: 'span[data-inline-heading]',
        getAttrs: element => ({
          level: Number(element.getAttribute('data-inline-heading')) === 3 ? 3 : 2
        })
      }
    ]
  },
  renderHTML ({ HTMLAttributes }) {
    const { level: headingLevel, ...attributes } = HTMLAttributes
    const level = headingLevel === 3 ? 3 : 2
    const style = level === 2
      ? 'font-size: 22px; font-weight: 600; line-height: 1.35;'
      : 'font-size: 18px; font-weight: 600; line-height: 1.4;'
    return ['span', mergeAttributes(attributes, {
      'data-inline-heading': level,
      style
    }), 0]
  }
})

const AudioNode = Node.create({
  name: 'audio',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes () {
    return {
      src: { default: null },
      controls: { default: true },
      style: { default: 'display: block; margin: 16px auto; max-width: 100%;' }
    }
  },
  parseHTML () {
    return [{
      tag: 'audio',
      getAttrs: dom => ({
        src: dom.getAttribute('src'),
        controls: dom.hasAttribute('controls'),
        style: dom.getAttribute('style') || 'display: block; margin: 16px auto; max-width: 100%;'
      })
    }]
  },
  renderHTML ({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes }
    if (attrs.controls !== false) attrs.controls = 'controls'
    if (!attrs.style) attrs.style = 'display: block; margin: 16px auto; max-width: 100%;'
    return ['audio', attrs]
  }
})

const VideoNode = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes () {
    return {
      src: { default: null },
      controls: { default: true },
      style: { default: 'display: block; max-width: 100%; max-height: 600px; margin: 16px auto;' }
    }
  },
  parseHTML () {
    return [{
      tag: 'video',
      getAttrs: dom => ({
        src: dom.getAttribute('src'),
        controls: dom.hasAttribute('controls'),
        style: dom.getAttribute('style') || 'display: block; max-width: 100%; max-height: 600px; margin: 16px auto;'
      })
    }]
  },
  renderHTML ({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes }
    if (attrs.controls !== false) attrs.controls = 'controls'
    if (!attrs.style) attrs.style = 'display: block; max-width: 100%; max-height: 600px; margin: 16px auto;'
    return ['video', attrs]
  }
})

export default {
  name: 'my_ueditor',
  components: {
    BubbleMenu,
    EditorContent
  },
  props: ['value'],
  data () {
    return {
      editor: null,
      content: this.value || '',
      uploadPrepareResp: null,
      imageUploading: false,
      upPercent: 0,
      toolbarVersion: 0,
      fontOptions: [
        { label: '宋体', value: 'SimSun' },
        { label: '黑体', value: 'SimHei' },
        { label: '微软雅黑', value: 'Microsoft YaHei' },
        { label: '楷体', value: 'KaiTi' },
        { label: '仿宋', value: 'FangSong' },
        { label: 'Arial', value: 'Arial' },
        { label: 'Times New Roman', value: 'Times New Roman' }
      ],
      fontSizeOptions: ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px'],
      linkDialogVisible: false,
      isToolbarSticky: false,
      toolbarWidth: 'auto',
      toolbarLeft: '0px',
      toolbarHeight: 42,
      scrollListener: null,
      resizeListener: null,
      articleKeywords: '',
      articleSearchLoading: false,
      articleSearchDone: false,
      articleSearchResults: [],
      articleSearchPage: 1,
      articleSearchCount: 5,
      articleSearchTotal: 0,
      articleSearchRequestId: 0,
      articleSearchTimer: null
    }
  },
  computed: {
    stickyToolbarStyle () {
      if (!this.isToolbarSticky) return {}
      return {
        position: 'fixed',
        top: '0px',
        left: this.toolbarLeft,
        width: this.toolbarWidth,
        zIndex: 1000
      }
    },
    canUndo () {
      return !!(this.editor && this.editor.can().undo())
    },
    canRedo () {
      return !!(this.editor && this.editor.can().redo())
    }
  },
  mounted () {
    this.initEditor()
    this.fetchUploadPrepare()
    this.scrollListener = () => {
      this.handleStickyScroll()
    }
    this.resizeListener = () => {
      this.handleStickyResize()
    }
    window.addEventListener('scroll', this.scrollListener, true)
    window.addEventListener('resize', this.resizeListener)
    this.$nextTick(() => {
      this.handleStickyScroll()
    })
  },
  beforeDestroy () {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true)
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener)
    }
    if (this.articleSearchTimer) clearTimeout(this.articleSearchTimer)
    this.articleSearchRequestId += 1
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  },
  watch: {
    value (nextValue) {
      this.content = nextValue || ''
      if (!this.editor) return
      const current = this.editor.getHTML()
      if ((nextValue || '') !== current) {
        this.editor.commands.setContent(nextValue || '', false)
      }
    },
    articleKeywords (keywords) {
      if (this.articleSearchTimer) clearTimeout(this.articleSearchTimer)
      this.articleSearchRequestId += 1
      this.articleSearchLoading = false
      this.articleSearchDone = false
      this.articleSearchResults = []
      this.articleSearchTotal = 0
      if (!this.linkDialogVisible || this.isLinkUrl(keywords)) return
      this.articleSearchTimer = setTimeout(() => {
        this.articleSearchTimer = null
        this.searchArticles(1)
      }, 500)
    }
  },
  methods: {
    handleStickyScroll () {
      if (!this.$refs.editorRoot || !this.$refs.toolbar) return
      const rootRect = this.$refs.editorRoot.getBoundingClientRect()
      const toolbarHeight = this.$refs.toolbar.offsetHeight || this.toolbarHeight
      this.toolbarHeight = toolbarHeight

      if (rootRect.top <= 0 && rootRect.bottom > toolbarHeight + 60) {
        this.isToolbarSticky = true
        this.toolbarWidth = rootRect.width + 'px'
        this.toolbarLeft = rootRect.left + 'px'
      } else {
        this.isToolbarSticky = false
      }
    },
    handleStickyResize () {
      if (this.$refs.editorRoot) {
        const rootRect = this.$refs.editorRoot.getBoundingClientRect()
        this.toolbarWidth = rootRect.width + 'px'
        this.toolbarLeft = rootRect.left + 'px'
      }
    },
    initEditor () {
      this.editor = new Editor({
        extensions: [
          StarterKit,
          TextStyle,
          Color,
          FontFamily,
          FontSize,
          InlineHeading,
          Underline,
          Link.configure({ openOnClick: false }),
          Image.configure({ inline: false }),
          AudioNode,
          VideoNode
        ],
        content: this.value || '',
        onUpdate: ({ editor }) => {
          this.content = editor.getHTML()
          this.$emit('getContent', this.content)
        },
        onTransaction: () => {
          this.toolbarVersion += 1
        }
      })
    },
    async fetchUploadPrepare () {
      try {
        const res = await this.$http.get('/admin/upload_prepare')
        if (res && res.code === 0) {
          this.uploadPrepareResp = res.data
        }
      } catch (e) {}
    },
    getEditor () {
      return this.editor
    },
    insertHtml (html) {
      if (!this.editor) return
      this.editor.chain().focus().insertContent(html || '').run()
      this.content = this.editor.getHTML()
      this.$emit('getContent', this.content)
    },
    insertMedia (url, mediaType) {
      if (!this.editor) return
      const safeUrl = url || ''
      const node = mediaType === 'audio'
        ? {
          type: 'audio',
          attrs: {
            src: safeUrl,
            controls: true,
            style: 'display: block; margin: 16px auto; max-width: 100%;'
          }
        }
        : {
          type: 'video',
          attrs: {
            src: safeUrl,
            controls: true,
            style: 'display: block; max-width: 100%; max-height: 600px; margin: 16px auto;'
          }
        }
      this.editor.chain().focus().insertContent(node).run()
      this.content = this.editor.getHTML()
      this.$emit('getContent', this.content)
    },
    isActive (name, attributes = {}) {
      if (!this.editor) return false
      return this.editor.isActive(name, attributes)
    },
    getTextStyleAttribute (name) {
      // Keep toolbar values in sync with the current selection.
      if (this.toolbarVersion < 0 || !this.editor) return ''
      return this.editor.getAttributes('textStyle')[name] || ''
    },
    setFontFamily (fontFamily) {
      if (!this.editor) return
      const chain = this.editor.chain().focus()
      if (fontFamily) {
        chain.setFontFamily(fontFamily).run()
      } else {
        chain.unsetFontFamily().run()
      }
    },
    setFontSize (fontSize) {
      if (!this.editor) return
      const chain = this.editor.chain().focus()
      if (fontSize) {
        chain.setMark('textStyle', { fontSize }).run()
      } else {
        chain.setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run()
      }
    },
    setTextColor (color) {
      if (!this.editor) return
      const chain = this.editor.chain().focus()
      if (color) {
        chain.setColor(color).run()
      } else {
        chain.unsetColor().run()
      }
    },
    shouldShowBubbleMenu ({ editor, state }) {
      const { selection } = state
      const quickMenu = this.$el && this.$el.querySelector('.quick_edit_menu')
      const menuFocused = !!(quickMenu && quickMenu.contains(document.activeElement))
      return editor.isEditable &&
        (editor.isFocused || menuFocused) &&
        selection instanceof TextSelection &&
        !selection.empty
    },
    toggleQuickLink () {
      if (!this.editor) return
      if (this.editor.isActive('link')) {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
      } else {
        this.openLinkDialog()
      }
    },
    openLinkDialog () {
      if (!this.editor) return
      this.linkDialogVisible = true
      this.articleKeywords = ''
      this.$nextTick(() => {
        if (this.$refs.articleLinkInput) this.$refs.articleLinkInput.focus()
        this.searchArticles(1)
      })
    },
    async searchArticles (page = 1) {
      if (this.articleSearchTimer) {
        clearTimeout(this.articleSearchTimer)
        this.articleSearchTimer = null
      }
      if (this.isLinkUrl(this.articleKeywords)) return
      const requestId = ++this.articleSearchRequestId
      this.articleSearchLoading = true
      this.articleSearchPage = page
      try {
        const res = await this.$http.get('/admin/content/list', {
          category_id: 0,
          keywords: this.articleKeywords,
          sort_by: 1,
          news_index: process.env.VUE_APP_NEWS_INDEX,
          page,
          count: this.articleSearchCount
        })
        if (requestId !== this.articleSearchRequestId) return
        this.articleSearchDone = true
        if (res && res.code === 0) {
          this.articleSearchResults = (res.data && res.data.list) || []
          this.articleSearchTotal = (res.data && res.data.total) || 0
        } else {
          this.articleSearchResults = []
          this.articleSearchTotal = 0
          this.$message && this.$message.error((res && res.msg) || '文章搜索失败')
        }
      } catch (error) {
        if (requestId !== this.articleSearchRequestId) return
        this.articleSearchDone = true
        this.articleSearchResults = []
        this.articleSearchTotal = 0
        this.$message && this.$message.error('文章搜索失败')
      } finally {
        if (requestId === this.articleSearchRequestId) {
          this.articleSearchLoading = false
        }
      }
    },
    selectArticleLink (article) {
      if (!article || !article.url) {
        this.$message && this.$message.error('该文章没有可用链接')
        return
      }
      this.applyLink(article.url)
    },
    handleLinkInputEnter () {
      if (this.isLinkUrl(this.articleKeywords)) {
        this.applyLink(this.normalizeLinkUrl(this.articleKeywords))
      } else {
        this.searchArticles(1)
      }
    },
    isLinkUrl (value) {
      const input = (value || '').trim()
      return /^(https?:\/\/|mailto:|tel:|www\.|\/)/i.test(input) ||
        /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/:?#].*)?$/i.test(input)
    },
    normalizeLinkUrl (value) {
      const url = (value || '').trim()
      return /^(?:www\.|(?:[a-z0-9-]+\.)+[a-z]{2,})/i.test(url) ? `https://${url}` : url
    },
    formatArticleDate (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      if (Number.isNaN(date.getTime())) return ''
      const day = String(date.getDate()).padStart(2, '0')
      const month = date.toLocaleString('en-US', { month: 'short' })
      return `${day} ${month} ${date.getFullYear()}`
    },
    applyLink (url) {
      if (!this.editor) return
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
      this.linkDialogVisible = false
    },
    resetLinkDialog () {
      if (this.articleSearchTimer) {
        clearTimeout(this.articleSearchTimer)
        this.articleSearchTimer = null
      }
      this.articleSearchRequestId += 1
      this.articleSearchLoading = false
      this.articleSearchDone = false
      this.articleSearchResults = []
      this.articleSearchPage = 1
      this.articleSearchTotal = 0
      this.articleKeywords = ''
    },
    clearTextFormatting () {
      if (!this.editor) return
      this.editor.chain().focus().unsetAllMarks().run()
    },
    toggleBold () {
      if (!this.editor) return
      this.editor.chain().focus().toggleBold().run()
    },
    toggleItalic () {
      if (!this.editor) return
      this.editor.chain().focus().toggleItalic().run()
    },
    toggleUnderline () {
      if (!this.editor) return
      this.editor.chain().focus().toggleUnderline().run()
    },
    toggleStrike () {
      if (!this.editor) return
      this.editor.chain().focus().toggleStrike().run()
    },
    toggleBulletList () {
      if (!this.editor) return
      this.editor.chain().focus().toggleBulletList().run()
    },
    toggleOrderedList () {
      if (!this.editor) return
      this.editor.chain().focus().toggleOrderedList().run()
    },
    toggleBlockquote () {
      if (!this.editor) return
      this.editor.chain().focus().toggleBlockquote().run()
    },
    toggleInlineHeading (level) {
      if (!this.editor) return
      this.editor.chain().focus().toggleMark('inlineHeading', { level }).run()
    },
    setLink () {
      if (!this.editor) return
      const previousUrl = this.editor.getAttributes('link').href || ''
      const url = window.prompt('请输入链接地址', previousUrl)
      if (url === null) return
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    },
    unsetLink () {
      if (!this.editor) return
      this.editor.chain().focus().unsetLink().run()
    },
    triggerImagePick () {
      if (this.$refs.imageInput) this.$refs.imageInput.click()
    },
    async handleImagePicked (e) {
      const files = (e && e.target && e.target.files) ? e.target.files : []
      const file = files[0]
      if (!file) return
      if (this.$refs.imageInput) this.$refs.imageInput.value = ''

      if (!this.uploadPrepareResp) await this.fetchUploadPrepare()
      if (!this.uploadPrepareResp || !this.uploadPrepareResp.up_token) {
        this.$message && this.$message.error('获取上传凭证失败')
        return
      }

      const fileNameArr = (file.name || '').split('.')
      const ext = fileNameArr.length > 1 ? fileNameArr[fileNameArr.length - 1] : 'png'
      const key = 'image/' + this.getDateString() + '/' + md5(file.name + Date.now()) + '.' + ext
      const putExtra = { fname: key }
      const config = { useCdnDomain: true, region: qiniu.region.z0 }

      this.imageUploading = true
      this.upPercent = 0

      const observable = qiniu.upload(file, key, this.uploadPrepareResp.up_token, putExtra, config)
      const next = (res) => { this.upPercent = Math.ceil(res.total.percent) }
      const error = (err) => {
        this.imageUploading = false
        this.$message && this.$message.error(err.message || '上传失败')
      }
      const complete = (res) => {
        this.imageUploading = false
        const url = (this.uploadPrepareResp.res_domain || '') + res.key
        if (this.editor) this.editor.chain().focus().setImage({ src: url }).run()
        this.content = this.editor ? this.editor.getHTML() : this.content
        this.$emit('getContent', this.content)
        this.fetchUploadPrepare()
      }
      observable.subscribe({ next, error, complete })
    },
    getDateString () {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}${month}${day}`
    },
    undo () {
      if (!this.editor) return
      this.editor.chain().focus().undo().run()
    },
    redo () {
      if (!this.editor) return
      this.editor.chain().focus().redo().run()
    }
  }
}
</script>

<style scoped>
.tiptap_editor_root {
  width: 100%;
}

.tiptap_toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 8px;
  border: 1px solid #dcdfe6;
  border-bottom: 1px solid #ebeef5;
  border-radius: 4px 4px 0 0;
  background: #ffffff;
  box-sizing: border-box;
  transition: box-shadow 0.2s ease;
}

.tiptap_toolbar.is_sticky {
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid #dcdfe6;
}

.tiptap_toolbar_placeholder {
  width: 100%;
  box-sizing: border-box;
}

.text_style_controls {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.font_family_select {
  width: 126px;
}

.font_size_select {
  width: 96px;
}

.quick_edit_menu {
  display: inline-flex;
  align-items: center;
  padding: 5px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
}

.quick_edit_group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.quick_edit_button {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #303133;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: 32px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.quick_edit_button:hover,
.quick_edit_button:focus-visible {
  outline: none;
  background: #f2f6fc;
  color: #409eff;
}

.quick_edit_button.active {
  background: #ecf5ff;
  color: #409eff;
}

.quick_edit_italic {
  font-style: italic;
}

.quick_edit_underline {
  text-decoration: underline;
}

.quick_edit_heading {
  font-size: 13px;
}

.quick_edit_text_button {
  font-size: 13px;
  font-weight: 500;
}

.quick_font_family_select {
  width: 112px;
  margin: 0 2px;
}

.quick_font_size_select {
  width: 88px;
  margin: 0 2px;
}

.quick_font_family_select :deep(.el-input__inner),
.quick_font_size_select :deep(.el-input__inner) {
  height: 32px;
  padding-left: 9px;
  border-color: transparent;
  line-height: 32px;
}

.quick_font_family_select:hover :deep(.el-input__inner),
.quick_font_size_select:hover :deep(.el-input__inner),
.quick_font_family_select :deep(.el-input.is-focus .el-input__inner),
.quick_font_size_select :deep(.el-input.is-focus .el-input__inner) {
  border-color: #dcdfe6;
}

.quick_font_family_select :deep(.el-input__icon),
.quick_font_size_select :deep(.el-input__icon) {
  line-height: 32px;
}

.quick_edit_divider {
  width: 1px;
  height: 20px;
  margin: 0 3px;
  background: #dcdfe6;
}

.article_link_input :deep(.el-input__inner) {
  height: 38px;
  border-color: #22c55e;
  border-radius: 6px;
  line-height: 38px;
}

.article_link_input :deep(.el-input__inner:focus) {
  border-color: #16a34a;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.12);
}

.article_link_loading {
  line-height: 38px;
}

.article_link_section_title {
  margin: 20px 16px 8px;
  color: #98a2b3;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
}

.article_link_results {
  min-height: 52px;
  max-height: 320px;
  overflow-y: auto;
}

.article_link_result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 42px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #ffffff;
  color: #202124;
  text-align: left;
  cursor: pointer;
}

.article_link_result:hover,
.article_link_result:focus {
  outline: none;
  background: #f3f4f6;
  color: #111827;
}

.article_link_title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.article_link_meta {
  flex: none;
  margin-left: 20px;
  color: #98a2b3;
  font-size: 12px;
}

.article_link_empty {
  padding: 28px 16px;
  color: #909399;
  text-align: center;
}

.tiptap_editor {
  min-height: 580px;
  padding: 16px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 0 0 4px 4px;
  background: #ffffff;
  overflow: auto;
}

.tiptap_editor :deep(.ProseMirror) {
  outline: none;
  min-height: 560px;
  max-width: 720px;
  margin: 0 auto;
  padding: 20px 10px 40px;
  line-height: 1.8;
  font-size: 18px;
  color: #2f2f2f;
  text-align: left;
  letter-spacing: 0.2px;
  word-break: break-word;
  white-space: pre-wrap;
  background: #ffffff;
}

.tiptap_editor :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
}

.tiptap_editor :deep(.ProseMirror audio) {
  display: block;
  width: min(100%, 500px);
  margin: 16px auto;
}

.tiptap_editor :deep(.ProseMirror video) {
  display: block;
  max-width: 100%;
  max-height: 600px;
  width: auto;
  margin: 16px auto;
  border-radius: 4px;
}

.tiptap_editor :deep(.ProseMirror p) {
  margin: 0 0 1em;
}

.tiptap_editor :deep(.ProseMirror h1) {
  font-size: 26px;
  line-height: 1.3;
  margin: 0 0 0.8em;
}

.tiptap_editor :deep(.ProseMirror h2) {
  font-size: 22px;
  line-height: 1.35;
  margin: 1.2em 0 0.8em;
}

.tiptap_editor :deep(.ProseMirror blockquote) {
  margin: 0 0 1em;
  padding: 0.2em 0 0.2em 1em;
  border-left: 3px solid #dcdfe6;
  color: #6b6b6b;
}

.tiptap_editor :deep(.ProseMirror ul),
.tiptap_editor :deep(.ProseMirror ol) {
  margin: 0 0 1em 1.2em;
}

.tiptap_editor :deep(.ProseMirror a) {
  color: #0f66d0;
  text-decoration: none;
}

.tiptap_editor :deep(.ProseMirror a:hover) {
  text-decoration: underline;
}
</style>

<style>
@font-face {
  font-family: 'KaiTi';
  src: local('KaiTi'), local('STKaiti'), local('Kaiti SC'), local('楷体');
}
@font-face {
  font-family: 'FangSong';
  src: local('FangSong'), local('STFangsong'), local('Fangsong SC'), local('仿宋');
}
@font-face {
  font-family: 'SimSun';
  src: local('SimSun'), local('Songti SC'), local('STSong'), local('宋体');
}
@font-face {
  font-family: 'SimHei';
  src: local('SimHei'), local('Heiti SC'), local('STHeiti'), local('黑体');
}

.link_picker_dialog {
  overflow: hidden;
  border-radius: 9px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.2);
}

.link_picker_dialog .el-dialog__header,
.link_picker_dialog .el-dialog__footer {
  display: none;
}

.link_picker_dialog .el-dialog__body {
  padding: 14px 10px 12px;
}
</style>
